import { Route, TrendingUp, Map, AlertTriangle, Quote } from "lucide-react";

// Plain-English explainer for lane optimization & revenue per mile (guide Step/Mission 11).
export default function LaneOptimizationExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="lane-optimization-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Route className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">Lane optimization &amp; revenue per mile</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        Being <strong>busy</strong> isn't the same as being <strong>profitable</strong>. Smart carriers choose lanes by
        what they earn per mile after costs — not by how many miles they run.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">RPM &amp; deadhead</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Revenue per mile = load pay ÷ <strong>total</strong> miles, including the empty "deadhead" miles to reach
            it. A $2.50/mi load with 150 deadhead miles can pay less than a shorter $2.20/mi load. Always price in the
            empty miles.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Map className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Choosing lanes</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Pick high-density freight lanes near your base, plan <strong>backhauls</strong> to cut deadhead, and factor
            terrain, tolls, fuel and seasonal hazards (mountains, weather). Track RPM on every lane so you learn which
            ones actually pay.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          Chasing every cheap load just to "stay busy" burns fuel, hours and truck wear on runs that lose money after
          expenses. <strong>High miles ≠ high profit</strong> — that's how owner-operators quit exhausted and broke.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Know your true cost per mile first (that's what the CPM Calculator is for), then only take loads that clear it
          with real margin. This isn't financial advice.
        </p>
      </div>
    </div>
  );
}
